<script setup lang="ts">
/**
 * Drawer — slide-in form panel from the right edge.
 *
 * Replaces the prototype's #drawer + #backdrop pattern (lines 7394-
 * 7470 of streama-cms.html). Built on Reka UI's Dialog primitive
 * (formerly Radix Vue) which gives us:
 *   - Focus trap inside the drawer
 *   - ESC to close
 *   - Click-outside-to-close (via overlay)
 *   - Restore focus to trigger on close
 *   - aria-modal=true on the dialog
 *
 * The visual treatment is custom (Streama dark surface with crimson
 * accent border on the title row) but the accessibility plumbing is
 * Reka's.
 *
 * Usage:
 *   <Drawer v-model:open="creating" title="New user" subtitle="...">
 *     <YourForm @submit="..." />
 *     <template #footer>
 *       <Button kind="ghost" @click="creating = false">Cancel</Button>
 *       <Button kind="primary">Save</Button>
 *     </template>
 *   </Drawer>
 *
 * The footer slot is optional — forms can render their own actions
 * inline if preferred. The default body slot is unstyled so each
 * form owns its layout.
 */
import {
  DialogRoot,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from 'reka-ui'
import { X } from 'lucide-vue-next'
import { useAttrs } from 'vue'

interface Props {
  /** Open state, two-way bound. */
  open: boolean
  title: string
  subtitle?: string
  /** Drawer width. Default 'md' = ~520px; 'lg' = ~720px for wide forms. */
  size?: 'sm' | 'md' | 'lg'
}
defineOptions({ inheritAttrs: false })
withDefaults(defineProps<Props>(), {
  size: 'md',
})
defineEmits<{
  'update:open': [value: boolean]
}>()

const attrs = useAttrs()
</script>
<template>
  <DialogRoot
    :open="open"
    @update:open="(v: boolean) => $emit('update:open', v)"
  >
    <DialogPortal>
      <DialogOverlay
        class="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm
               data-[state=open]:animate-in data-[state=closed]:animate-out
               data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      />
      <DialogContent
        v-bind="attrs"
        :class="[
          'fixed right-0 top-0 bottom-0 z-50 flex flex-col',
          'bg-surface border-l border-border-strong shadow-lg',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
          'duration-200 ease-out',
          'focus:outline-none',
          { sm: 'w-[420px]', md: 'w-[520px]', lg: 'w-[720px]' }[size],
          'max-w-full',
        ]"
      >
        <!-- Title bar with crimson accent stripe -->
        <header
          class="px-5 py-4 border-b border-border flex items-start gap-3 shrink-0
                 relative"
        >
          <span
            class="absolute left-0 top-3 bottom-3 w-0.5 bg-brand"
            aria-hidden="true"
          />
          <div class="flex-1 min-w-0">
            <DialogTitle
              class="text-base font-semibold text-text-1 m-0 leading-tight"
            >{{ title }}</DialogTitle>
            <DialogDescription
              v-if="subtitle"
              class="mt-1 text-xs text-text-3 m-0 leading-relaxed"
            >{{ subtitle }}</DialogDescription>
          </div>
          <DialogClose
            class="shrink-0 w-7 h-7 flex items-center justify-center
                   text-text-4 hover:text-text-1 hover:bg-surface-2
                   rounded transition-colors
                   focus-visible:outline-none focus-visible:shadow-focus"
            :aria-label="$t('common.close')"
          >
            <X :size="14" />
          </DialogClose>
        </header>
        <!-- Body -->
        <div class="flex-1 overflow-y-auto px-5 py-4">
          <slot />
        </div>
        <!-- Footer (optional) -->
        <footer
          v-if="$slots.footer"
          class="px-5 py-3 border-t border-border shrink-0
                 flex items-center justify-end gap-2"
        >
          <slot name="footer" />
        </footer>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
